import React, { useState } from 'react'
import { Modal, Flex, Spin, Space, Tooltip } from 'antd'
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons'
import { ASSETS_APPLICATION_BASE } from '../config/api'

// 判断文件是否为Office文档
const isOfficeFile = (fileName) => {
    if (!fileName) return false;
    const ext = fileName.toLowerCase().split('.').pop();
    return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
};

// 判断文件是否为图片
const isImageFile = (fileName) => {
    if (!fileName) return false;
    const ext = fileName.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
};

// 判断文件是否为PDF
const isPdfFile = (fileName) => {
    if (!fileName) return false;
    const ext = fileName.toLowerCase().split('.').pop();
    return ext === 'pdf';
};

/**
 * 附件预览组件
 * @param {Array|string} fileNames - 文件显示名称（数组或字符串）
 * @param {Array|string} fileUrls - 文件实际名称/路径（数组或字符串）
 * @param {string} baseUrl - 文件基础URL
 */
function FilePreview({ fileNames, fileUrls, baseUrl = `${ASSETS_APPLICATION_BASE}/` }) {
    // 文件预览相关状态
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');
    const [previewLoading, setPreviewLoading] = useState(true);

    // 处理预览点击
    const handlePreview = (fileName, imageName) => {
        const fileUrl = `${baseUrl}${imageName}`;
        
        if (isOfficeFile(fileName)) {
            // 使用微软 Office Online Viewer 在新窗口打开预览
            const viewerUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;
            window.open(viewerUrl, '_blank');
        } else if (isImageFile(fileName) || isPdfFile(fileName)) {
            // 图片和PDF在 Modal 中显示
            setPreviewUrl(fileUrl);
            setPreviewTitle(fileName);
            setPreviewLoading(true);
            setPreviewOpen(true);
        } else {
            // 其他文件在新窗口打开
            window.open(fileUrl, '_blank');
        }
    };

    // 处理下载点击
    const handleDownload = (fileName, imageName) => {
        const fileUrl = `${baseUrl}${imageName}`;
        // 创建一个隐藏的 a 标签来触发下载
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // 渲染单个文件项
    const renderFileItem = (fileName, imageName, index) => {
        return (
            <Space key={index} size={10} style={{ marginRight: 16, marginBottom: 4 }}>
                <span style={{ color: '#000' }}>{fileName}</span>
                <Tooltip title="预览">
                    <EyeOutlined 
                        style={{ color: '#1890ff', cursor: 'pointer', fontSize: 14 }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handlePreview(fileName, imageName);
                        }}
                    />
                </Tooltip>
                <Tooltip title="下载">
                    <DownloadOutlined 
                        style={{ color: '#1890ff', cursor: 'pointer', fontSize: 14 }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDownload(fileName, imageName);
                        }}
                    />
                </Tooltip>
            </Space>
        );
    };

    // 渲染文件列表
    const renderFileList = () => {
        if (!fileNames || !fileUrls) return null;

        // 处理数组情况
        if (Array.isArray(fileNames)) {
            return fileNames.map((fileName, index) => {
                const imageName = Array.isArray(fileUrls) ? fileUrls[index] : fileUrls;
                return renderFileItem(fileName, imageName, index);
            });
        }

        // 处理单个文件情况
        return renderFileItem(fileNames, fileUrls, 0);
    };

    return (
        <>
            <Flex wrap="wrap" gap="4px">
                {renderFileList()}
            </Flex>
            
            {/* 文件预览 Modal */}
            <Modal
                open={previewOpen}
                onCancel={() => {
                    setPreviewOpen(false);
                    setPreviewUrl('');
                }}
                title={previewTitle}
                width={1600}
                footer={null}
                centered
                destroyOnHidden
            >
                <Spin spinning={previewLoading} tip="加载中...">
                    {isImageFile(previewTitle) ? (
                        <img 
                            src={previewUrl} 
                            alt={previewTitle}
                            style={{
                                width: '100%',
                                maxHeight: '70vh',
                                objectFit: 'contain'
                            }}
                            onLoad={() => setPreviewLoading(false)}
                        />
                    ) : (
                        <iframe
                            src={previewUrl}
                            style={{
                                width: '100%',
                                height: '70vh',
                                border: 'none'
                            }}
                            title={previewTitle}
                            onLoad={() => setPreviewLoading(false)}
                        />
                    )}
                </Spin>
            </Modal>
        </>
    );
}

export default FilePreview
